import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { zh, en } from "./loaded";
import type { Locale, Translations } from "./types";

const LOCALE_STORAGE_KEY = "bastion-locale";

// 将原始 JSON 加载转换为类型化的 Translations 接口。
// JSON 文件是我们的真实数据源；接口提供编译时类型安全。
const ZH = zh as unknown as Translations;
const EN = en as unknown as Translations;

interface I18nContextValue {
  locale: Locale;
  /** 翻译一个键，可选地传入插值参数如 `{name}`。 */
  t: (key: keyof Translations, params?: Record<string, string | number>) => string;
  /** 解析列表类型的翻译（如加载界面提示）。 */
  tList: (key: keyof Translations) => string[];
  /** 根据 id 解析实体名称（塔、敌人、Boss、技能、地图）。 */
  tEntity: (
    kind: "tower" | "enemy" | "boss" | "skill" | "map" | "tower_position" | "tower_skill",
    id: string,
  ) => string;
  tAchievement: (id: string) => { name: string; description: string };
  setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function getInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === "zh" || stored === "en") return stored;
  } catch {
    // 存储不可用
  }
  const browserLang = typeof navigator !== "undefined" ? navigator.language.toLowerCase() : "";
  if (browserLang.startsWith("zh")) return "zh";
  return "en";
}

/** 在模板字符串中替换 `{key}` 占位符。 */
function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const val = params[key];
    return val !== undefined ? String(val) : `{${key}}`;
  });
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within an I18nProvider");
  return ctx;
}

export interface I18nProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? getInitialLocale());

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, l);
    } catch {
      // 存储不可用——静默忽略。
    }
  }, []);

  const dict = locale === "zh" ? ZH : EN;

  const t = useCallback(
    (key: keyof Translations, params?: Record<string, string | number>): string => {
      const val = dict[key];
      if (Array.isArray(val)) return ""; // arrays (tips) are accessed directly
      return interpolate(val as string, params);
    },
    [dict],
  );

  const tList = useCallback(
    (key: keyof Translations): string[] => {
      const val = dict[key];
      return Array.isArray(val) ? val : [];
    },
    [dict],
  );

  /**
   * 根据 id 解析实体名称/描述（塔、敌人、Boss、技能、地图、塔定位、塔技能）。
   * 若未找到翻译则回退到原始 id。
   */
  const tEntity = useCallback(
    (
      kind: "tower" | "enemy" | "boss" | "skill" | "map" | "tower_position" | "tower_skill",
      id: string,
    ): string => {
      const section = dict.entity[kind];
      const translated = section[id];
      if (typeof translated === "string" && translated.length > 0) return translated;
      return id; // fallback to raw id
    },
    [dict],
  );

  /**
   * 根据 id 解析成就名称。
   * 若未找到翻译则回退到原始 id。
   */
  const tAchievement = useCallback(
    (id: string): { name: string; description: string } => {
      const entry = dict.entity.achievement[id];
      if (entry && typeof entry === "object") {
        if (typeof entry.name === "string" && entry.name.length > 0) {
          return {
            name: entry.name,
            description: typeof entry.description === "string" ? entry.description : "",
          };
        }
      }
      return { name: id, description: "" }; // fallback to raw id
    },
    [dict],
  );

  return (
    <I18nContext.Provider value={{ locale, t, tList, tEntity, tAchievement, setLocale }}>
      {children}
    </I18nContext.Provider>
  );
}
